import React from 'react'
import { Location } from 'history'
import queryString from 'query-string'

// Contants
import { RouteProps, routes, QueryParams } from 'constants/routes'

// Components
import { useModalContext } from 'containers/ModalManager/ModalContext'
import SignUpForm from 'components/SignUpForm'
import RedirectBadge from 'components/RedirectBadge'
import IndexLayout, { Title } from 'components/IndexLayout'
import SignUpBreadcrumbs from '../../components/SignUpBreadcrumbs'
import SketchLoginDeprecationModal from '../../modals/SketchLoginDeprecationModal'

// Utils
import { useQueryParams } from 'utils'
import { trackEvent } from 'modules/analytics'

// Styles
import { Link, FormContainer } from './SignUpView.styles'
import { useFlag } from 'constants/flags'

// GQL
import { useAcceptWorkspaceInviteMutation } from 'gqlTypes'

// Types
interface LocationState {
  from: Location
}
interface SignUpViewProps extends RouteProps<'SIGN_UP'> {
  location: Location<LocationState>
}

const SignUpView: React.FC<SignUpViewProps> = ({ history, location }) => {
  const isOnboardingLicensesSignupEnabled = useFlag(
    'onboarding-licenses-signup'
  )
  const { state } = location
  const { showModal } = useModalContext()

  const queryParams = useQueryParams<'SIGN_UP'>()
  const linkState = { from: state?.from }

  const willContinueToWorkspaceCreation =
    queryParams?.skipWorkspaceCreation !== 'true' &&
    (!location.state?.from ||
      location.state.from.pathname === routes.WORKSPACE_CREATE.create({}))

  const [acceptWorkspaceInvite] = useAcceptWorkspaceInviteMutation({
    // As a temporary solution we call the mutation and proceed without
    // handling any possible errors.
    // https://github.com/sketch-hq/Cloud/issues/10236
    onError: 'do-nothing',
  })
  const renderForm = () => {
    return (
      <SignUpForm
        onCompleted={() => {
          if (queryParams.sketch === 'true') {
            showModal(SketchLoginDeprecationModal)
          }

          // Check if the user has a workspace invitation to accept
          if (
            linkState.from?.pathname === routes.WORKSPACE_INVITE.template() &&
            linkState.from?.search
          ) {
            const searchQuery: { token?: string } = queryString.parse(
              linkState.from.search
            )

            if (searchQuery.token) {
              acceptWorkspaceInvite({
                variables: {
                  membershipToken: searchQuery.token,
                },
              })
            }
          }

          // TODO: Remove usages of 'onboarding-licenses-signup' feature flag
          // https://github.com/sketch-hq/Cloud/issues/10012
          if (isOnboardingLicensesSignupEnabled) {
            trackEvent('SIGN UP - Sign up form success', {
              formContext: 'main',
            })

            history.push(routes.CHECK_YOUR_INBOX.create({}))
          } else {
            trackEvent('SIGN UP - Sign up form success', {
              willContinueToWorkspaceCreation: willContinueToWorkspaceCreation,
              formContext: 'main',
            })

            // Handle "skipWorkspaceCreation" query param for the next step
            const query: QueryParams<'ABOUT_YOU'> = {}
            if (!willContinueToWorkspaceCreation) {
              query.skipWorkspaceCreation = 'true'
            }

            history.push(
              routes.ABOUT_YOU.create({
                query,
              })
            )
          }
        }}
      />
    )
  }

  return (
    <IndexLayout
      headerLink="sign-in"
      variant="green"
      isOnboardingLicensesSignupEnabled>
      {state?.from && <RedirectBadge from={state.from} />}

      {isOnboardingLicensesSignupEnabled ? (
        <>
          <Title>Your Best Work Starts Here</Title>
          <FormContainer>{renderForm()}</FormContainer>
        </>
      ) : (
        <>
          <SignUpBreadcrumbs
            showWorkspaceCrumbs={willContinueToWorkspaceCreation}
            currentCrumb={0}
          />
          <Title>Create a Sketch Account</Title>
          {renderForm()}
        </>
      )}

      <Link
        variant="secondary"
        to={{
          pathname: routes.SSO_SIGN_IN.create({}),
          state: linkState,
        }}
        onClick={() => {
          trackEvent('SIGN UP - "Sign in with SSO" link opened', {
            formContext: 'main',
          })
        }}
        isUnderlined={isOnboardingLicensesSignupEnabled}>
        Sign in with SSO
      </Link>
    </IndexLayout>
  )
}

export default SignUpView
